import { PathsManifest } from '@zg-rentals/paths-manifest';
import React from 'react';
import { normalizeUser } from '../user/userUtils';
import { ThemeProvider } from 'styled-components';
import { ThemeConstellation } from '@zillow/constellation';
import { IconFont } from '@zillow/zrs-zsg-icon';
import { RentalManagerUserInfoWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client';
import type { RentalManagerEnvironment, RentalManagerNodeEnv } from '@zg-rentals/rental-manager-env-base';
import { RentalManagerBrowserEnvironment } from '@zg-rentals/rental-manager-env-base';
import { ScreenType } from '@zg-rentals/rental-manager-env-base';
import { debounce } from '@zg-rentals/zdash';
import type { RentalManagerAuthCheckUserInfoFeaturesDto } from '@zg-rentals/ts-rental-manager-api-client';
import { browserUtils } from '../utils/browserUtils';
import { createLogger } from '@zg-rentals/logger-base';

const defaultPathsManifest = new PathsManifest('/rental-manager', 'zillow');
export const defaultPaths = defaultPathsManifest.getAllPaths();

export const defaultNodeEnv: RentalManagerNodeEnv = {
  isProduction: false,
  zgEnv: '',
  zgApi: '',
  zgApiPublic: '',
  nodeEnv: '',
  metroidAlicornHppConfigId: '',
  metroidAlicornUrl: '',
  ip: '',
  userAgent: '',
  hostname: '',
  brand: 'zillow',
  isInMobileWebView: false,
  originalServerSideRequestCookies: {},
  apiResponseServerSideRequestCookies: [],
  buildId: '',
  basePath: '',
  gmapsApiKey: __GMAPS_API_KEY__,
  protocol: '',
  remotePort: '',
};

const calculateScreenSizes = () => {
  const screenWidth = browserUtils.screen.width();
  const constellationScreenSize = browserUtils.screen.size(screenWidth);
  const screenType = browserUtils.screen.type(constellationScreenSize);
  const isScreenDesktop = screenType === ScreenType.DESKTOP;
  return { isScreenDesktop, screenWidth, constellationScreenSize, screenType };
};

export const makeDefaultEnvironment = (): RentalManagerEnvironment => ({
  appName: typeof __APP_NAME__ !== 'undefined' ? __APP_NAME__ : 'unknown-app',
  isClient: typeof window !== 'undefined',
  isServer: typeof window === 'undefined',
  brand: 'zillow',
  paths: defaultPaths,
  nodeEnv: defaultNodeEnv,
  user: normalizeUser(),
  logger: createLogger(),
  isNodeLess: false,
  initialAuthComplete: false,
  ...calculateScreenSizes(),
});

export type RentalManagerEnvironmentContextType = {
  environment: RentalManagerEnvironment;
  setEnvironment: (newEnvironment: RentalManagerEnvironment) => void;
};

export const RentalManagerEnvironmentContext = React.createContext({
  environment: makeDefaultEnvironment(),

  setEnvironment: (newEnvironment: RentalManagerEnvironment) => {},
});

export type RentalManagerEnvironmentProviderProps = {
  children?: React.ReactNode;
  environment: RentalManagerEnvironment;
};

export const RentalManagerEnvironmentProvider = ({ children, environment }: RentalManagerEnvironmentProviderProps) => {
  const [contextValue, setContext] = React.useState<RentalManagerEnvironment>({
    ...makeDefaultEnvironment(),
    ...environment,
    ...calculateScreenSizes(),
  });

  const handleResize = () => {
    setContext((oldContext) => ({
      ...oldContext,
      ...calculateScreenSizes(),
    }));
  };

  const throttleFn = React.useMemo(() => debounce(handleResize, 250), []);

  const handleThrottledResize = React.useCallback(() => throttleFn(), [throttleFn]);

  React.useEffect(() => {
    if (!contextValue.logger) {
      return;
    }
    // sync environment
    RentalManagerBrowserEnvironment.setInstance(contextValue);
  }, [contextValue]);

  React.useEffect(() => {
    window.addEventListener('resize', handleThrottledResize);
    if (environment.isNodeLess && !environment.initialAuthComplete) {
      RentalManagerUserInfoWebApiHandler_get({
        includeCustomers: true,
        // @ts-ignore
        includeSummary: true,
        includeCanClaimFreeListings: true,
      })
        .then((apiRes) => {
          apiRes.features = apiRes.features || ({} as RentalManagerAuthCheckUserInfoFeaturesDto);
          setContext({
            ...contextValue,
            user: apiRes,
            initialAuthComplete: true,
          });
          return;
        })
        .catch(() => {
          setContext({
            ...contextValue,
            initialAuthComplete: true,
          });
        });
    }
    return () => {
      window.removeEventListener('resize', handleThrottledResize);
    };
  }, []);

  return (
    <RentalManagerEnvironmentContext.Provider
      value={{
        environment: contextValue,
        setEnvironment: setContext,
      }}
    >
      <IconFont />
      <ThemeProvider theme={ThemeConstellation}>{children}</ThemeProvider>
    </RentalManagerEnvironmentContext.Provider>
  );
};
